<!-- [팝업] 간편처방등록 검색 (게시 종료된 간편처방등록만 검색) -->
<template>
  <b-modal
    v-model="showDrugLandingSelect"
    cancel-variant="outline-secondary"
    centered
    ok-title="추가"
    cancel-title="취소"
    size="xl"
    :title="'간편처방 '+ (isExclude ? '제외':'포함') + ' 간편처방등록 추가'"
    no-close-on-esc
    no-close-on-backdrop
    @ok.prevent="submit()"
    @close.prevent="close()"
    @cancel.prevent="close()"
  >
    <b-row
      class="mt-25"
    >
      <b-col
        cols="12"
        md="10"
        class="d-flex align-items-center justify-content-start"
      >
        <b-form-input
          v-model="query.liveInfoId"
          class="el-def mr-1"
          placeholder="ID"
          style="width:10%"
          @keyup.enter="refetchData()"
        />
        <v-select
          v-model="query.clientId"
          :options="codes.clients"
          :reduce="option => option.code"
          class="el-def mr-1"
          placeholder="제약사"
          style="width:200px;"
        />
        <b-form-input
          v-model="query.title"
          class="el-def mr-1"
          placeholder="제목"
          style="width:40%;"
          @keyup.enter="refetchData()"
        />
        <v-select
          v-model="query.isAfterConsultPopup"
          :options="codes.useTypeFixed"
          :reduce="option => option.code"
          class="el-def mr-1"
          placeholder="진료후팝업"
          style="width:200px;"
        />
        <b-form-datepicker
          v-model="query.startDate"
          class="el-def mr-1"
          placeholder="게시기간(Fr)"
          style="width:250px;"
        />
        ~
        <b-form-datepicker
          v-model="query.endDate"
          class="el-def ml-1"
          placeholder="게시기간(To)"
          style="width:250px;"
        />
      </b-col>
      <!-- Search -->
      <b-col
        cols="12"
        md="2"
        class="d-flex align-items-center justify-content-end"
      >
        <b-button
          variant="primary"
          class="btn-search"
          @click.prevent="refetchData()"
        >
          조회
        </b-button>
      </b-col>
    </b-row>
    <b-row class="mt-25">
      <span class="ml-25">게시 종료된 간편처방등록 리스트 [{{ totalRecords }}]건</span>
    </b-row>
    <b-row class="mt-1">
      <b-table
        ref="refDrugLandingTable"
        primary-key="liveInfoId"
        :items="fetchDrugLandings"
        :fields="tableColumns"
        :sort-by.sync="sortBy"
        :sort-desc.sync="isSortDesc"
        responsive
        sticky-header="460px"
        selectable
        select-mode="multi"
        class="position-relative"
        @row-selected="onRowSelectedDrugLanding"
        @row-dblclicked="onRowDblClickedDrugLanding"
      >
        <template #cell(isAfterConsultPopup)="data">
          <div
            class="text-nowrap"
            style="text-align: center;"
          >
            {{ data.value ? 'O' : 'X' }}
          </div>
        </template>
        <template #cell(liveDate)="data">
          <span class="text-nowrap">
            {{ data.value }}
          </span>
        </template>
      </b-table>
    </b-row>
    <b-row>
      <b-col cols="12">
        <b-pagination
          v-model="currPage"
          :total-rows="totalRecords"
          limit="10"
          per-page="10"
          align="center"
        />
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
import dayjs from "dayjs"
import axios from '@axios'
import {
  ref, onMounted, watch, getCurrentInstance,
} from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { errorFormatter } from '@core/utils/filter'
import vSelect from "vue-select"

export default {
  components: {
    vSelect,
  },

  props: {
    showDrugLandingSelect: {
      type: Boolean,
      required: true,
    },
    selectedValue: {
      type: Array,
      required: false,
      default: () => [],
    },
    isExclude: {
      type: Boolean,
      required: false,
      default: false,
    },
    filterType: {
      type: Number,
      required: true,
    },
  },

  setup(props, { emit }) {
    onMounted(() => {
      fetchCodes()
    })
    const instance = getCurrentInstance()
    const bvModal = instance.proxy.$bvModal

    const toast = useToast()

    const initQuery = () => ({
      liveInfoId: null,
      clientId: null,
      title: null,
      isAfterConsultPopup: null,
      startDate: dayjs().add(-6, 'month').format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
    })
    // Query Data
    const query = ref(initQuery())
    // Query Data End.

    // Set Codes
    const codes = ref({
      clients: [],
      postStatusType: [],
      useTypeFixed: [],
    })

    // 게시종료 상태 코드 (codes 응답에서 label로 조회)
    const closedLiveType = ref(null)

    const fetchCodes = () => {
      axios.get("/fa/druglanding/codes")
        .then(rs => {
          const { clients, postStatusType, useTypeFixed } = rs.data
          codes.value.clients = clients
          codes.value.postStatusType = postStatusType
          codes.value.useTypeFixed = useTypeFixed

          // 게시종료 코드값 조회 (공백 제거 후 비교)
          const closed = postStatusType?.find(x => (x.label || '').replace(/\s/g, '') === '게시종료')
          closedLiveType.value = closed ? closed.code : 3
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: '코드 데이터를 불러오는데 실패하였습니다.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }
    // Set Codes End.

    // Main Table Config
    const refDrugLandingTable = ref(null)
    const sortBy = ref('liveInfoId')
    const isSortDesc = ref(true)
    const currPage = ref(1)
    const totalRecords = ref(0)
    const tableColumns = [
      {
        key: 'liveInfoId', label: 'ID', sortable: true, thStyle: { width: '10%' },
      },
      {
        key: 'clientName', label: '제약사명', sortable: true, thStyle: { width: '20%' },
      },
      { key: 'title', label: '제목', sortable: true },
      {
        key: 'isAfterConsultPopup', label: '진료후팝업', sortable: true, thStyle: { width: '100px' },
      },
      {
        key: 'liveDate', label: '게시기간', sortable: true, thStyle: { width: '240px' },
      },
    ]

    const sortFieldMap = {
      liveDate: 'startDate',
    }
    const refetchData = () => {
      if (currPage.value !== 1) {
        currPage.value = 1
      } else {
        refDrugLandingTable.value.refresh()
      }
    }
    watch([currPage], () => {
      refDrugLandingTable.value.refresh()
    })

    const selectedRows = ref([])
    const onRowSelectedDrugLanding = items => {
      selectedRows.value = items
    }
    const onRowDblClickedDrugLanding = item => {
      selectedRows.value = [item]
      submit()
    }
    // Main Table Config End.

    const fetchDrugLandings = (ctx, callback) => {
      axios.post("/fa/druglanding/list", {
        search: {
          query: {
            ...query.value,
            liveType: closedLiveType.value, // 게시종료 고정
          },
        },
        sort: {
          predicate: sortFieldMap[sortBy.value] || sortBy.value,
          reverse: isSortDesc.value,
        },
        pagination: {
          number: currPage.value,
          count: 10,
        },
      })
        .then(rs => {
          const { items, totalRecord } = rs.data
          callback(items)
          totalRecords.value = totalRecord
        })
        .catch(error => {
          callback([])
          totalRecords.value = 0
          toast({
            component: ToastificationContent,
            props: {
              title: errorFormatter(error, '간편처방등록 데이터 조회에 실패하였습니다.'),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    const close = () => {
      query.value = initQuery()
      currPage.value = 1
      emit('close', null)
    }

    const submit = () => {
      if (!selectedRows.value || selectedRows.value.length === 0) {
        toast({
          component: ToastificationContent,
          props: {
            title: '간편처방등록을 선택하세요',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      // 이미 추가된 항목을 제외한 신규 선택 개수
      const newCount = selectedRows.value.filter(
        item => !props.selectedValue.includes(item.liveInfoId),
      ).length

      if (props.selectedValue.length + newCount > 5) {
        bvModal.msgBoxOk('포함/제외 각각 최대 5개까지 추가할 수 있습니다.', {
          okTitle: '확인',
        })
        return
      }

      const selectedList = []
      selectedRows.value.forEach(item => {
        selectedList.push({
          filterId: item.liveInfoId,
          filterType: props.filterType,
          isExclude: props.isExclude,
          clientName: item.clientName,
          title: item.title,
          isAfterConsultPopup: item.isAfterConsultPopup ?? null,
          period: item.liveDate,
        })
      })

      emit('close', selectedList)
    }

    return {
      query,
      codes,
      fetchCodes,
      close,
      submit,
      refDrugLandingTable,
      sortBy,
      isSortDesc,
      currPage,
      totalRecords,
      tableColumns,
      refetchData,
      onRowSelectedDrugLanding,
      onRowDblClickedDrugLanding,
      fetchDrugLandings,
    }
  },
}
</script>
